import {
  BookOpen,
  Pencil,
  ScrollText,
  Trash2,
} from "lucide-react";
import type {
  CommentResponse,
  RuleResponse,
  KnowledgeItemResponse,
  BatchResponse,
} from "../../gen/models";
import { ThreadCommentEditor } from "./ThreadCommentEditor";

interface ThreadCommentCardProps {
  comment: CommentResponse;
  rulesList: RuleResponse[];
  knowledgeList: KnowledgeItemResponse[];
  batch?: BatchResponse;
  isHighlighted: boolean;
  isEditing: boolean;
  editingText: string;
  editingRuleIds: string[];
  editingKnowledgeIds: string[];
  savingEdit: boolean;
  mentionType: "rule" | "knowledge" | null;
  mentionQuery: string;
  mentionTarget: "composer" | "editor" | null;
  onCardClick: (comment: CommentResponse) => void;
  onStartEdit: (comment: CommentResponse) => void;
  onCancelEdit: () => void;
  onSaveEdit: (commentId: string) => void;
  onDeleteComment: (commentId: string) => void;
  onEditTextChange: (text: string) => void;
  onSelectMentionRule: (rule: RuleResponse) => void;
  onSelectMentionKnowledge: (knowledge: KnowledgeItemResponse) => void;
  onRemoveEditingRule: (ruleId: string) => void;
  onRemoveEditingKnowledge: (knowledgeId: string) => void;
  onInspectRule?: (ruleId: string, rule?: RuleResponse) => void;
  onInspectKnowledge?: (knowledgeId: string, knowledge?: KnowledgeItemResponse) => void;
}

export function ThreadCommentCard({
  comment: c,
  rulesList,
  knowledgeList,
  batch,
  onInspectRule,
  onInspectKnowledge,
  isHighlighted,
  isEditing,
  editingText,
  editingRuleIds,
  editingKnowledgeIds,
  savingEdit,
  mentionType,
  mentionQuery,
  mentionTarget,
  onCardClick,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteComment,
  onEditTextChange,
  onSelectMentionRule,
  onSelectMentionKnowledge,
  onRemoveEditingRule,
  onRemoveEditingKnowledge,
}: ThreadCommentCardProps) {
  if (isEditing) {
    return (
      <ThreadCommentEditor
        comment={c}
        rulesList={rulesList}
        knowledgeList={knowledgeList}
        editingText={editingText}
        editingRuleIds={editingRuleIds}
        editingKnowledgeIds={editingKnowledgeIds}
        savingEdit={savingEdit}
        mentionType={mentionType}
        mentionQuery={mentionQuery}
        mentionTarget={mentionTarget}
        onCancelEdit={onCancelEdit}
        onSaveEdit={onSaveEdit}
        onEditTextChange={onEditTextChange}
        onSelectMentionRule={onSelectMentionRule}
        onSelectMentionKnowledge={onSelectMentionKnowledge}
        onRemoveEditingRule={onRemoveEditingRule}
        onRemoveEditingKnowledge={onRemoveEditingKnowledge}
      />
    );
  }

  const isBatchLocked = batch?.status === "completed" || batch?.status === "processing";

  return (
    <div
      id={`comment-card-${c.id}`}
      onClick={() => onCardClick(c)}
      className={`p-2.5 rounded-xl text-xs space-y-1.5 cursor-pointer transition-all duration-150 border-l-2 ${
        isHighlighted
          ? "border-amber-600 dark:border-amber-500 bg-amber-200 dark:bg-amber-950/40 shadow-xs"
          : "border-stone-500 dark:border-stone-700/80 bg-stone-300/80 hover:bg-stone-300 dark:bg-stone-900/60 dark:hover:bg-stone-800/80"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between text-[11px] text-stone-600 dark:text-stone-400">
        <span className="font-medium flex items-center gap-1.5">
          {c.user_name && <span className="font-semibold text-stone-800 dark:text-stone-200">{c.user_name}</span>}
          <span>{new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        </span>
        {!isBatchLocked && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit(c);
              }}
              className="text-stone-400 hover:text-stone-200 p-0.5 transition"
              title="Edit comment"
            >
              <Pencil className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteComment(c.id);
              }}
              className="text-stone-400 hover:text-rose-400 p-0.5 transition"
              title="Delete comment"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>



      {/* Comment text */}
      <p className="text-stone-900 dark:text-stone-200 leading-relaxed whitespace-pre-wrap">
        {c.comment_text}
      </p>

      {/* Footer tags (mentions & batch) */}
      <div className="flex flex-wrap items-center gap-2 pt-0.5">
        {c.rule_ids?.map((rid) => {
          const r = rulesList.find((x) => x.id === rid);
          if (onInspectRule) {
            return (
              <button
                key={rid}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onInspectRule(rid, r);
                }}
                className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-500 dark:text-amber-400 hover:underline cursor-pointer"
                title={`Inspect rule ${r?.title || rid}`}
              >
                <ScrollText className="w-2.5 h-2.5" />
                <span>{r?.title || "Rule"}</span>
              </button>
            );
          }
          return (
            <span
              key={rid}
              className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-400"
            >
              <ScrollText className="w-2.5 h-2.5" />
              <span>{r?.title || "Rule"}</span>
            </span>
          );
        })}

        {c.knowledge_item_ids?.map((kid) => {
          const k = knowledgeList.find((x) => x.id === kid);
          if (onInspectKnowledge) {
            return (
              <button
                key={kid}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onInspectKnowledge(kid, k);
                }}
                className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-indigo-500 dark:text-indigo-400 hover:underline cursor-pointer"
                title={`Inspect knowledge item ${k?.title || kid}`}
              >
                <BookOpen className="w-2.5 h-2.5" />
                <span>{k?.title || "Knowledge"}</span>
              </button>
            );
          }
          return (
            <span
              key={kid}
              className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-indigo-400"
            >
              <BookOpen className="w-2.5 h-2.5" />
              <span>{k?.title || "Knowledge"}</span>
            </span>
          );
        })}

        {batch ? (
          <span
            className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ml-auto ${
              batch.status === "completed"
                ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                : "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800"
            }`}
          >
            Batch: {batch.title}
            {batch.status === "completed" && " (completed)"}
          </span>
        ) : (
          <span className="text-[9px] font-medium text-stone-400 dark:text-stone-500 ml-auto">
            Unbatched
          </span>
        )}
      </div>
    </div>
  );
}
